import PasswordInput from "@/components/form/PasswordInput"
import TextInput from "@/components/form/TextInput"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { getErrorMessage } from "@/utils/ErrorUtils"
import { useSetLoading } from "@/utils/useLoader"
import { useModalHelper } from "@/utils/useModalHelper"
import React from "react"
import { useForm } from "react-hook-form"
import { useQuery } from "react-query"
import { Link } from "react-router-dom"
import { CSSTransition, SwitchTransition } from "react-transition-group"
import { useAuthStore } from "../../context/AuthContext"
import { useSiteSettingOptions } from "../../context/SiteSettingsContext"
import { ThruFormProvider } from "../../context/ThruFormContext"
import { apiUrl } from "../../utils/config"
import { validateEmail } from "../../utils/Validate"
import { KEY_LIST_LOGIN_MOTD, listLoginMotd } from "../thru-admin/thru-admin-login-motd/ThruAdminLoginMotdClient"
import ForgotLoginModal from "./ForgotLoginModal"
import MfaCodeChallange from "./MfaCodeChallange"
import MfaRememberDevice from "./MfaRememberDevice"

const inputs = {
  USERNAME: "username",
  PASSWORD: "password",
}

const Login: React.FC = () => {
  const { handleSubmit: handleSubmitSaml } = useForm()

  const { data: loginMotdList } = useQuery([KEY_LIST_LOGIN_MOTD], () => listLoginMotd())
  const activeMotd =
    loginMotdList?.filter(
      (motd) =>
        window.location.origin.includes(motd.domainName) &&
        (!motd.activeStartDate || motd.activeStartDate <= new Date()) &&
        (!motd.activeEndDate || motd.activeEndDate >= new Date()),
    ) ?? []

  const login = useAuthStore((state) => state.login)
  const authState = useAuthStore((state) => state.authState)
  const mfaCachedCredentials = useAuthStore((state) => state.mfaCachedCredentials)
  const authError = useAuthStore((state) => state.authError)

  const {
    state: { siteSettings },
  } = useSiteSettingOptions()

  const setLoading = useSetLoading()
  const samlIsEnabled = siteSettings?.data?.samlLoginIsEnabled

  const onSubmit = async (data: any) => {
    const credentials = {
      username: data[inputs.USERNAME],
      password: data[inputs.PASSWORD],
    }

    setLoading(true)
    await login(credentials)
    setLoading(false)
  }

  const onSamlSubmit = async (data: any) => {
    const returnUrl = data?.returnUrl
    const samlSSOurl = `${apiUrl}/saml/InitiateSingleSignOn${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ""}`
    window.location.replace(samlSSOurl)
  }

  const loginRef = React.useRef(null)
  const challengeAuthRef = React.useRef(null)
  const rememberDeviceRef = React.useRef(null)
  const nodeRef: any =
    authState === "mfa-code-sent" ? loginRef : authState === "mfa-code-verified" ? rememberDeviceRef : challengeAuthRef

  const { openModal: openForgotUsernameModal } = useModalHelper({ modalId: "forgotUsernameModal" })
  const { openModal: openForgotPasswordModal } = useModalHelper({ modalId: "forgotPasswordModal" })

  return (
    <div className="thru-login-container m-0 flex p-0 flex-wrap">
      <div
        className="left thru-grid-item flex flex-col justify-between flex-1 shrink basis-[min(400px,100vw)]"
        id="first-grid-item"
      >
        <div className="px-8">
          <img className="img-fluid" src="\images\AFT-alt.svg" alt="Boomi logo" width={350} />
        </div>
        <div className="thru-login-centerTitles justify-center">
          <h3 className="thru-login-title">Secure and simplify file transfers.</h3>
          <h4 className="thru-login-subtitle">
            Boomi Managed File Transfer (MFT) automates the secure exchange of sensitive file-based data within and
            across organizations, helping businesses meet industry compliance and governance requirements.
          </h4>
          <div className="flex justify-center">
            <img className="img-fluid" src="\images\login-ui_2.svg" alt="Homepage Preview" />
          </div>
        </div>

        <div className="thru-login-footer">
          <div className="thru-login-info">
            <div className="thru-login-copyright">
              <small>Copyright © {new Date().getFullYear()} Thru, Inc.</small>
            </div>
            <div className="thru-login-menu">
              <a target="_blank" rel="noreferrer" href="https://www.thruinc.com/support" className="thru-link">
                Support
              </a>
              <a target="_blank" rel="noreferrer" href="https://www.thruinc.com/contact-us/" className="thru-link">
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col items-center justify-center bg-source/20 py-8 relative grow basis-[min(400px,100vw)]">
        <ForgotLoginModal
          modalId="forgotUsernameModal"
          title="Recover Your Username"
          prompt={`Enter the email associated with your account and we'll send you a reminder.`}
          inputLabel="Email"
          inputPlaceholder="Enter your email"
          validate={(value: any) => (!validateEmail(value) ? "Invalid email format." : true)}
        />
        <ForgotLoginModal
          modalId="forgotPasswordModal"
          title="Recover Your Password"
          prompt={`Enter your username and we'll send you a link to reset your password.`}
          inputLabel="Username"
          inputPlaceholder="Enter your username"
        />
        <div className="si-bg w-full h-[min(100%,600px)] flex items-center justify-center">
          <div className="thru-login-form-mobile z-[1]">
            <Card className="mx-auto min-w-[360px] max-w-sm overflow-hidden py-6 px-2">
              <SwitchTransition mode={"out-in"}>
                <CSSTransition
                  key={authState}
                  nodeRef={nodeRef}
                  addEndListener={(done: any) => {
                    nodeRef.current.addEventListener("transitionend", done, false)
                  }}
                  classNames="fade"
                >
                  <div ref={nodeRef}>
                    <div className="login-container">
                      {authState === "mfa-code-sent" && mfaCachedCredentials ? (
                        <MfaCodeChallange
                          username={mfaCachedCredentials.username}
                          password={mfaCachedCredentials.password}
                          sid={mfaCachedCredentials.sid}
                          email={mfaCachedCredentials.email}
                        />
                      ) : authState === "mfa-code-verified" ? (
                        <MfaRememberDevice />
                      ) : (
                        <>
                          <CardHeader className="text-center">
                            <CardTitle className="text-3xl">Login</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid gap-4">
                              <ThruFormProvider onSubmit={onSubmit} loading={false} preventUnsavedChanges={false}>
                                <div className="grid gap-4">
                                  <div className="grid gap-2">
                                    <div className="flex items-center">
                                      <Label htmlFor={inputs.PASSWORD}>Username</Label>
                                      <Link
                                        to="#"
                                        className="ml-auto inline-block text-sm underline"
                                        onClick={openForgotUsernameModal}
                                        id="forgot-username-button"
                                        tabIndex={6}
                                      >
                                        Forgot username?
                                      </Link>
                                    </div>
                                    <TextInput
                                      autoFocus
                                      name={inputs.USERNAME}
                                      placeholder="m@example.com"
                                      required
                                      tabIndex={1}
                                    />
                                  </div>
                                  <div className="grid gap-2">
                                    <div className="flex items-center">
                                      <Label htmlFor={inputs.PASSWORD}>Password</Label>
                                      <Link
                                        to="#"
                                        className="ml-auto inline-block text-sm underline"
                                        onClick={openForgotPasswordModal}
                                        id="forgot-password-button"
                                        tabIndex={7}
                                      >
                                        Forgot password?
                                      </Link>
                                    </div>
                                    <PasswordInput name={inputs.PASSWORD} required newPassword={false} tabIndex={2} />
                                  </div>

                                  <Button id="btnLogin" type="submit" className="w-full" tabIndex={4}>
                                    Login
                                  </Button>
                                </div>
                              </ThruFormProvider>

                              {!!authError && <p className="text-sm text-red-600">{getErrorMessage(authError)}</p>}

                              {activeMotd?.length > 0 && (
                                <div className="space-y-4 system-messages-container">
                                  <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                      <span className="w-full border-t" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                      <span className="bg-background px-2 text-muted-foreground">System Messages</span>
                                    </div>
                                  </div>
                                  {activeMotd.map((motd) => (
                                    <div
                                      key={motd.id}
                                      className="rounded-md bg-muted/50 px-3 py-2 text-center text-sm"
                                      style={motd.textColor ? { color: motd.textColor } : undefined}
                                    >
                                      {motd.message}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {samlIsEnabled ? (
                                <form key={2} onSubmit={handleSubmitSaml(onSamlSubmit)}>
                                  <div className="relative mb-4">
                                    <div className="absolute inset-0 flex items-center">
                                      <span className="w-full border-t" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                      <span className="bg-background px-2 text-muted-foreground">
                                        SSO Corporate Login
                                      </span>
                                    </div>
                                  </div>
                                  <Button
                                    id="btnSamlLogin"
                                    type="submit"
                                    variant="outline"
                                    className="w-full"
                                    tabIndex={5}
                                  >
                                    Corporate Login
                                  </Button>
                                </form>
                              ) : (
                                <></>
                              )}
                            </div>
                          </CardContent>
                        </>
                      )}
                    </div>
                  </div>
                </CSSTransition>
              </SwitchTransition>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
